import i18next, { type Resource, type ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, toSupportedLanguage, type Language } from './languages';
import en from './locales/en.json';
import ru from './locales/ru.json';
import uk from './locales/uk.json';

/** One translation namespace with a resource tree per supported language. */
export interface NamespaceResources {
  namespace: string;
  resources: Record<Language, ResourceLanguage>;
}

export const SHARED_NAMESPACE = 'shared';
export const sharedResources: NamespaceResources = { namespace: SHARED_NAMESPACE, resources: { ru, en, uk } };

const STORAGE_KEY = 'orbita.language';

export const i18n = i18next.createInstance();

function readStoredLanguage(): Language | undefined {
  try {
    return toSupportedLanguage(localStorage.getItem(STORAGE_KEY));
  } catch {
    return undefined;
  }
}

function applyDocumentLanguage(language: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

/**
 * Initializes i18next synchronously with every namespace (app, shared, one per module).
 * The initial language is the last one the user picked on this device, otherwise Russian.
 */
export function initI18n(namespaces: NamespaceResources[], language?: Language): typeof i18n {
  if (i18n.isInitialized) {
    for (const ns of namespaces) {
      for (const lng of SUPPORTED_LANGUAGES)
        i18n.addResourceBundle(lng, ns.namespace, ns.resources[lng], true, true);
    }
    return i18n;
  }
  const resources: Resource = {};
  for (const lng of SUPPORTED_LANGUAGES) {
    resources[lng] = {};
    for (const ns of namespaces) resources[lng][ns.namespace] = ns.resources[lng];
  }
  const lng = language ?? readStoredLanguage() ?? DEFAULT_LANGUAGE;

  void i18n.use(initReactI18next).init({
    resources,
    lng,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    ns: namespaces.map((n) => n.namespace),
    defaultNS: SHARED_NAMESPACE,
    fallbackNS: SHARED_NAMESPACE,
    initAsync: false,
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  });
  applyDocumentLanguage(lng);
  i18n.on('languageChanged', applyDocumentLanguage);
  return i18n;
}

/** Loads the namespaces of one language that are not part of the main bundle (the modules' texts). */
export type LanguageLoader = (language: Language) => Promise<Record<string, ResourceLanguage>>;

let languageLoader: LanguageLoader | null = null;
const loadedLanguages = new Map<Language, Promise<void>>();
const readyLanguages = new Set<Language>();

/** Registers where the lazily loaded namespaces come from (the runtime: one chunk per language with every module). */
export function registerLanguageLoader(loader: LanguageLoader | null): void {
  languageLoader = loader;
  loadedLanguages.clear();
  readyLanguages.clear();
}

/**
 * Makes sure the lazily loaded namespaces of `language` are in i18next (once per language; a failed load is tried
 * again next time). Resolves at once when nothing is loaded lazily (tests initialize every namespace).
 */
export function ensureLanguage(language: Language): Promise<void> {
  const loader = languageLoader;
  if (!loader) return Promise.resolve();
  let pending = loadedLanguages.get(language);
  if (!pending) {
    pending = loader(language).then((namespaces) => {
      for (const [namespace, resources] of Object.entries(namespaces))
        i18n.addResourceBundle(language, namespace, resources, true, true);
      readyLanguages.add(language);
    });
    loadedLanguages.set(language, pending);
    pending.catch(() => loadedLanguages.delete(language));
  }
  return pending;
}

/** Whether the lazily loaded namespaces of `language` are available (always true without a loader). */
export function isLanguageLoaded(language: Language): boolean {
  return !languageLoader || readyLanguages.has(language);
}

/** Switches the UI language (after loading its texts) and remembers it on this device. */
export async function setLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage unavailable (private mode): the choice still applies for this session.
  }
  // A language whose texts are in already switches at once (no await before it: callers and tests see it applied).
  if (!isLanguageLoaded(language)) await ensureLanguage(language);
  if (i18n.language !== language) await i18n.changeLanguage(language);
}

export function getLanguage(): Language {
  return toSupportedLanguage(i18n.language) ?? DEFAULT_LANGUAGE;
}
